/* @flow */
import * as F from 'lodash/fp';
import * as React from 'react';
import styled from 'styled-components';

import { Box, Footer } from '@sonyged/proc-ui';

import { CUSTOMER_WIDTH, DISH_GAP, HEIGHT, MIN_HEIGHT, MIN_WIDTH, WIDTH } from './constants';
import type { Customer, SimulationState, Sushi } from '../../rules/types';
import { Customer as CustomerView } from './Customer';
import { Mise01, Mise02, Mise03, MiseRepeat } from './Background';
import { ScalableContainer } from './ScalableContainer';
import { SushiContext } from '../../SushiContext';
import { SushiIconList } from './SushiIconList';
import { Sushi as SushiView, SushiDish } from './Sushi';
import { satisfiedCustomers } from '../../rules/exec';
import AppContext from '../../../javascripts/AppContext';
import DishesEaten from './DishesEaten';
import FinishModal from './FinishModal';
import StartModal from './StartModal';
import SushiMenu from '../../components/SushiMenu';

const SPEED = 3000 * 0.6;

type Props = {
  gameStates: Array<SimulationState>,
  customers: Array<Customer>,
  customerStartState?: ?Array<Sushi[]>,
  onNext: Function,
  half: 'first' | 'second',
  roundIndex: number,
  prevCost?: number,
  moreKanji?: boolean,
};

type State = {
  stateIndex: number,
  progress: number, // progress in [0, 1]
};

type Action = {
  type: 'setIndex',
  payload: [number, number],
};

const setIndex = (index: number, progress: number) => ({
  type: 'setIndex',
  payload: [index, progress],
});

const initialState = { stateIndex: 0, progress: 0 };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'setIndex':
      return { stateIndex: action.payload[0], progress: action.payload[1] };
    default:
      (action: empty);
      return state;
  }
}

export function Simulation(props: Props) {
  const { customers, gameStates, onNext, customerStartState, prevCost = 0, moreKanji = false, ...rest } = props;
  const maxStateIndex = gameStates.length - 1;

  const [scale, setScale] = React.useState(1);
  const [init, setInit] = React.useState(true);

  const [{ stateIndex, progress }, dispatch] = React.useReducer(reducer, initialState);

  let context = React.useContext(AppContext);
  React.useEffect(() => {
    if (!context) return;
    context.soundController.sePlay('sushi_start');
  }, [context]);

  const { t, belt, eatens } = gameStates[stateIndex];
  const previousEatens =
    stateIndex === 0
      ? customerStartState
        ? customerStartState
        : customers.map(() => [])
      : gameStates[stateIndex - 1].eatens;

  const stage = Math.floor(progress * 3); // 0 | 1 | 2

  const fullCustomers = React.useMemo(() => satisfiedCustomers(customers, gameStates[stateIndex]), [
    gameStates,
    stateIndex,
  ]);

  const customerStatus = React.useMemo(() => {
    let result = customers.map((customer, i) => {
      const currentAte = eatens[i];
      const prevAte = previousEatens[i] || [];
      const isFinish = fullCustomers.some(c => c === customer);

      if (currentAte.length > prevAte.length) {
        return {
          isFinish,
          customer: customer,
          eatens: prevAte,
          eating: currentAte[currentAte.length - 1],
        };
      } else {
        return { isFinish, customer: customer, eatens: prevAte, eating: null };
      }
    });

    if (result.length === 1) {
      result = [
        {
          isFinish: false,
          customer: null,
          eatens: [],
          eating: null,
        },
      ].concat(result);
    }

    return result;
  }, [eatens, previousEatens]);

  const beltLength = belt.length;

  const visibleSushiIndice = React.useMemo(() => {
    return [t - 1, t, t + 1, t + 2, t + 3, t + 4].map(x => (x + beltLength) % beltLength);
  }, [t, beltLength]);

  const visibleSushi = React.useMemo(() => {
    return visibleSushiIndice.map((index, i) => {
      const slot = belt[index];
      if (slot.type === 'filled')
        return (
          <SushiView
            {...slot.sushi}
            key={index}
            style={{ left: `${(visibleSushiIndice.length - 2 - i) * 20}%`, top: '56%' }}
          />
        );
      else return null;
    });
  }, [visibleSushiIndice, belt]);

  const eatenSushiDishes = React.useMemo(
    () => {
      return customerStatus.map(({ eatens }, i) => {
        return eatens.map((sushi, k) => (
          <SushiDish
            key={k}
            style={{ top: `${44 - ((k * DISH_GAP) / HEIGHT) * 100}%`, left: `${60 - i * 20}%` }}
            {...sushi}
          />
        ));
      });
    },
    customerStatus.map(s => s.eatens)
  );

  const start = React.useRef(-1);
  const loop = React.useCallback(
    (now: number) => {
      if (start.current < 0) {
        // first loop
        start.current = now;
        requestAnimationFrame(loop);
        return;
      }

      const t = now - start.current;
      const currentIndex = Math.floor(t / SPEED);
      const currentProgress = (t - currentIndex * SPEED) / SPEED;

      if (currentIndex > maxStateIndex) {
        dispatch(setIndex(maxStateIndex, 0.99));
        return;
      }

      dispatch(setIndex(currentIndex, currentProgress));

      requestAnimationFrame(loop);
    },
    [maxStateIndex]
  );

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      setInit(false);
      requestAnimationFrame(loop);
    }, 3000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  const eatingCustomerIndice = React.useMemo(() => {
    const eatingCustomerIndice = [];
    if (stage < 2) {
      for (let i = 0; i < customerStatus.length; i++) {
        if (customerStatus[i].eating) eatingCustomerIndice.push(i);
      }
    }
    return eatingCustomerIndice;
  }, [stage, customerStatus.length].concat(customerStatus.map(s => s.eating)));

  const allEatens = React.useMemo(() => F.flatten(eatens), [eatens]);

  const totalDishEatens = allEatens.length;

  const mapper = React.useContext(SushiContext);

  let allSushi = React.useMemo(() => {
    let { belt } = gameStates[0];
    return F.pipe(
      F.map(slot => (slot.type !== 'empty' ? slot.sushi : null)),
      F.filter(Boolean)
    )(belt);
  }, [gameStates]);

  let currentCost = React.useMemo(() => {
    return F.pipe(F.map(mapper.getSushiOriginalPrice), F.sum)(allSushi);
  }, [allSushi]);

  const profit = F.pipe(F.map(mapper.getSushiPrice), F.sum)(allEatens) - prevCost - currentCost;

  let menuText;
  if (props.half === 'first') {
    menuText = '組目 前半';
  }

  if (props.half === 'second') {
    menuText = '組目 後半';
  }

  return (
    <Box height="100%" position="relative">
      <SushiMenu
        exitLink={mapper.getExitLink()}
        query={'?skip_intro=true'}
        title={''}
        subTitle={mapper.getTitle()}
        menuText={props.roundIndex + 1 + menuText}
      />
      <MiseRepeat />
      <ScalableContainer
        {...rest}
        height="100%"
        dWidth={WIDTH}
        dHeight={HEIGHT}
        dMinWidth={MIN_WIDTH}
        dMinHeight={MIN_HEIGHT}
        align="top"
        onScaleChange={setScale}
      >
        <Mise01 />
        <AnimationContainer>
          {customerStatus.map(({ customer, isFinish, eating }, i) => {
            if (!customer) return null;
            const animating = eating && stage === 1;
            const finish = isFinish && (!eating || stage === 2);
            return (
              <CustomerView
                {...customer}
                key={i}
                animating={animating}
                isFinish={finish}
                sit={customers.length === 1 ? 'middle' : sitPositions[i]}
              />
            );
          })}
        </AnimationContainer>
        <Mise02 />
        <AnimationContainer style={{ transform: `translate3d(${progress * 20}%, 0, 0)` }}>
          {visibleSushi}
        </AnimationContainer>
        <AnimationContainer>
          {eatenSushiDishes.map((dishes, i) => (
            <React.Fragment key={i}>{dishes}</React.Fragment>
          ))}

          {customerStatus.map(({ eatens, eating }, i) => {
            if (!eating) return null;
            const left = `${60 - i * 20}%`;

            let Component = SushiView;
            let top = '';
            if (stage === 0) {
              Component = SushiView;
              top = `${56 - progress * 3 * (0.12 + (eatens.length * DISH_GAP) / HEIGHT) * 100}%`;
            } else if (stage === 1) {
              Component = SushiView;
              top = `${56 - (0.12 + (eatens.length * DISH_GAP) / HEIGHT) * 100}%`;
            } else {
              Component = SushiDish;
              top = `${44 - ((eatens.length * DISH_GAP) / HEIGHT) * 100}%`;
            }
            return <Component key={i} style={{ top, left }} {...eating} />;
          })}
        </AnimationContainer>
        <Mise03 />
      </ScalableContainer>
      <SushiIconList t={t} eatingCustomerIndice={eatingCustomerIndice} belt={belt} scale={scale} />

      <DishesEaten total={totalDishEatens} />

      <StartModal moreKanji={moreKanji} init={init} half={props.half} />

      <FinishModal
        customerCount={customers.length}
        moreKanji={moreKanji}
        finish={stateIndex === maxStateIndex && progress > 0.9}
        score={profit}
        half={props.half}
      />

      <Box position="absolute" bottom={0} left={0} right={0} className="sushi">
        {stateIndex === maxStateIndex && progress > 0.9 && (
          <Footer shadow={false} variant="raised">
            <Footer.MainButton onClick={onNext}>つぎへ</Footer.MainButton>
          </Footer>
        )}
      </Box>
    </Box>
  );
}

const sitPositions = {
  '0': 'right',
  '1': 'middle',
  '2': 'left',
};

const AnimationContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: `${((CUSTOMER_WIDTH * 5) / WIDTH) * 100}%`,
  left: `${((WIDTH - 5 * CUSTOMER_WIDTH) / 2 / WIDTH) * 100}%`,
});
